import { useState } from 'react';
import { useApolloClient, useMutation } from 'react-apollo';
import { useDispatch } from 'react-redux';
import uniqBy from 'lodash.uniqby';

import { AchievementsWithAccount } from '../../../../graphql/queries/achievements';
import { markAccountAchievementsAsViewed as markAccountAchievementsAsViewedMutation } from '../../../../graphql/mutations/achievements';
import { getErrorString } from '../../../../util/normalize-errors';
import { ACHIEVEMENT_UNLOCKED_MODAL } from '../../../../components/global/AchievementUnlocked';
import { openModal } from '../../../../actions/modal-actions';

/**
 * Handles checking whether an achievement has
 * been unlocked by the currently logged on user.
 *
 * @returns {{
 *  unlockedAccountAchievements: Array<Achievement>
 *  newUnlockedAccountAchievements: Array<Achievement>
 *  checkAchievementUnlocked: Function,
 *  checkAndReportAchievementUnlocked: Function,
 *  loading: boolean,
 *  error: string
 * }}
 */
const useAchievementUnlocked = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [
    newUnlockedAccountAchievements,
    setNewUnlockedAccountAchievements,
  ] = useState([]);
  const [
    unlockedAccountAchievements,
    setUnlockedAccountAchievements,
  ] = useState([]);
  const [pendingAchievementLevels, setPendingAchievementLevels] = useState([]);
  const client = useApolloClient();
  const [markAccountAchievementsAsViewed] = useMutation(
    markAccountAchievementsAsViewedMutation,
  );
  const dispatch = useDispatch();

  /**
   * Checks if a certain achievement has been unlocked based on some
   * user and/or background action performed.
   *
   * @param {Array<ACHIEVEMENT_ACTIONS>} actionsToCheck Achievement actions to be checked.
   * @param {boolean} useNetworkOnly Whether we should use network-only check or go with cache first..
   */
  const checkAchievementUnlocked = async (
    actionsToCheck,
    useNetworkOnly = false,
  ) => {
    setUnlockedAccountAchievements([]);
    setLoading(true);
    try {
      const { data: { me: user } = {} } = await client.query({
        query: AchievementsWithAccount,
        fetchPolicy: useNetworkOnly ? 'network-only' : 'cache-first',
      });

      const userAchievements = user?.achievements?.records ?? [];

      const unlockedAccountAchievementsFromActions = userAchievements.filter(
        accountAchievementLevel =>
          actionsToCheck.includes(
            accountAchievementLevel.achievement_level.achievement.action,
          ),
      );

      const newUnlockedAccountAchievementsRecords = unlockedAccountAchievementsFromActions.filter(
        achievement => !achievement.viewed_at,
      );

      if (newUnlockedAccountAchievementsRecords.length > 0) {
        // Mark them as seen NOW:

        await markAccountAchievementsAsViewed({
          variables: {
            input: {
              account_achievement_ids: newUnlockedAccountAchievementsRecords.map(
                ({ id }) => id,
              ),
            },
          },
        });
      }

      // It's only relevant to show a single achievement level per-action as pending
      // since levels go scaling up (1 -> 10 -> 100...etc), so the next pending
      // achievement level is enough to convey to the user.
      const uniqueByActionPendingAchievements = uniqBy(
        user.pending_achievement_levels.records,
        pendingAchievementLevel => pendingAchievementLevel.achievement.action,
      );

      setUnlockedAccountAchievements(unlockedAccountAchievementsFromActions);
      setNewUnlockedAccountAchievements(newUnlockedAccountAchievementsRecords);
      setPendingAchievementLevels(uniqueByActionPendingAchievements);

      return {
        unlockedAccountAchievements: unlockedAccountAchievementsFromActions,
        newUnlockedAccountAchievements: newUnlockedAccountAchievementsRecords,
        pendingAchievementLevels: uniqueByActionPendingAchievements ?? [],
      };
    } catch (err) {
      setError(getErrorString(err));
      return null;
    } finally {
      setLoading(false);
    }
  };

  const checkAndReportAchievementUnlocked = async actionsToCheck => {
    const {
      newUnlockedAccountAchievements: newUnlockedAccountAchievementsCurrent,
      pendingAchievementLevels: pendingAchievementLevelsCurrent,
    } = (await checkAchievementUnlocked(actionsToCheck, true)) || {};

    if ((newUnlockedAccountAchievementsCurrent?.length ?? 0) === 0) {
      return;
    }

    dispatch(
      openModal(ACHIEVEMENT_UNLOCKED_MODAL, {
        achievementLevels: newUnlockedAccountAchievementsCurrent.map(
          accountAchievement => accountAchievement.achievement_level,
        ),
        pendingAchievementLevels: pendingAchievementLevelsCurrent,
      }),
    );
  };

  return {
    pendingAchievementLevels,
    unlockedAccountAchievements,
    newUnlockedAccountAchievements,
    checkAchievementUnlocked,
    checkAndReportAchievementUnlocked,
    loading,
    error,
  };
};

export default useAchievementUnlocked;
